<template>
  <div
    id="timetable-preview-area"
    class="timetable-preview-area"
  >
    <div
      class="timetable-background"
      :style="timetableBackgroundColor"
    >
      <div
        class="this-month"
        :style="timetableFontColor"
      >
        {{ showPeriod ? presentPeriod : $formatDate(range.end, 'YYYY.MM') }}
      </div>

      <div
        v-show="logoImageBlob === null"
        class="timetable-logo"
        :style="timetableFontColor"
      >
        {{ title }}
      </div>
      <div
        v-show="logoImageBlob !== null"
        class="timetable-logo"
        :style="presentBackImage"
      />

      <div class="timetable-container">
        <div
          v-show="showFirstColumnTime"
          class="timetable-timeline"
        >
          <div class="day-in-week" />
          <template
            v-for="(courseTime, index) in courseTimes"
          >
            <TimelineBlock
              :start-time="courseTime[0]"
              :end-time="courseTime[1]"
              :course-start-end-times="courseStartEndTimes"
              :block-height="courseBlockHeight(courseTime)"
            />
            <div
              v-show="courseTime[1] !== courseTimes[courseTimes.length - 1][1]"
              :style="breakTimeStyle"
            />
          </template>
        </div>
        <div
          v-for="childCourseByDate in childCoursesByDate"
          v-show="!dayOfWeekVisible.includes(childCourseByDate[0])"
          class="timetable-day"
        >
          <div class="day-in-week">
            {{ dateStringToDayOfWeek(childCourseByDate[0]) }}
          </div>

          <template
            v-for="(courseTime, index) in courseTimes"
          >
            <ChildCoursesBlock
              :invisible-parent-courses="invisibleParentCourses"
              :show-course-time="showCourseTime"
              :show-instructors="showInstructors"
              :start-time="courseTime[0]"
              :end-time="courseTime[1]"
              :block-height="courseBlockHeight(courseTime)"
              :child-courses="childCourseByDate[1]"
              :day-of-week-visible="dayOfWeekVisible"
              :font-size="fontSize"
              :sub-font-size="subFontSize"
              @init="onInitChildCourse"
            />
            <div
              v-show="courseTime[1] !== courseTimes[courseTimes.length - 1][1]"
              :style="breakTimeStyle"
              style="background-color: rgb(197, 197, 197); padding-right: 5px;"
            />
          </template>
        </div>
        <div
          class="no-schedule-warning-message"
          v-show="childCoursesByDate.size === 0"
        >
          <warning-message
            message="등록된 스케줄이 없습니다. <br> <a href='#/course/schedule' style='color:#9b9b9b;font-weight:bold;'>스케줄 페이지</a>에서 수업을 먼저 등록해 주세요."
          />
        </div>
      </div>
      <div
        class="timetable-footer"
        :style="timetableFontColor"
      >
        <div class="center-info">
          {{ centerAccount.name }}<br>
          {{ centerAccount.address }}
        </div>
        <div class="fifl-inc">
          © FIFL Inc.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dateFns from 'date-fns';
import HourMinutes from './hourMinutes';
import { computedGetters } from '../../utils/vuex-helper';
import ChildCoursesBlock from './ChildCoursesBlock';
import BreakTimeBlock from './BreakTimeBlock';
import enLocale from 'date-fns/locale/en';
import _ from 'lodash';
import TimelineBlock from './TimelineBlock';
import Color from 'color';
import WarningMessage from '../../components/element-ui/WarningMessage';

const childCourseContainerHeight = 790;
const breakBlockContainerHeight = 60;

export default {
  name: 'TimeTablePreviewArea',
  components: {
    WarningMessage,
    TimelineBlock,
    BreakTimeBlock,
    ChildCoursesBlock
  },
  props: {
    childCoursesByDate: {},
    range: {},
    courseStartEndTimes: {},
    logoImageBlob: {}
  },
  data() {
    return {
      breakBlockContainerHeight
    };
  },
  computed: {
    ...computedGetters(['centerAccount']),
    ...computedGetters(
      [
        'dayOfWeekVisible',
        'backgroundColor',
        'showPeriod',
        'showCourseTime',
        'showInstructors',
        'showFirstColumnTime',
        'fontSize',
        'subFontSize',
        'title',
        'breakTimes',
        'invisibleParentCourses'
      ],
      'timeTable'
    ),
    courseTimes() {
      let breakTimes = this.breakTimes.map(breakTime => {
        return [
          new HourMinutes(breakTime.start).toMinutes(),
          new HourMinutes(breakTime.end).toMinutes()
        ];
      });

      let courseTimes = [
        [_.min(this.courseStartEndTimes), _.max(this.courseStartEndTimes)]
      ];

      return addBreakTimes(courseTimes, breakTimes);
    },
    presentBackImage() {
      return {
        backgroundImage: `url('${this.logoImageBlob}')`
      };
    },
    presentPeriod() {
      return `${dateFns.format(
        this.range.start,
        'YYYY.MM.DD'
      )} - ${dateFns.format(this.range.end, 'YYYY.MM.DD')}`;
    },
    breakTimeStyle() {
      return {
        height: `${breakBlockContainerHeight}px`
      };
    },
    timetableBackgroundColor() {
      return {
        backgroundColor: this.backgroundColor
      };
    },
    timetableFontColor() {
      const color = Color(this.backgroundColor);

      if (color.isDark()) {
        return {
          color: '#ffffff'
        };
      }

      return {
        color: color.darken(0.7)
      };
    }
  },
  methods: {
    courseBlockHeight(courseTime) {
      let breakTimes = 0;

      this.breakTimes.forEach(breakTime => {
        breakTimes +=
          HourMinutes.dateToMinutes(breakTime.end) -
          HourMinutes.dateToMinutes(breakTime.start);
      });

      let differenceTime =
        _.max(this.courseStartEndTimes) -
        _.min(this.courseStartEndTimes) -
        breakTimes;
      let differenceCourseTime = courseTime[1] - courseTime[0];

      return (
        ((childCourseContainerHeight -
          breakBlockContainerHeight * this.breakTimes.length) /
          differenceTime) *
        differenceCourseTime
      );
    },
    dateStringToDayOfWeek(dateString) {
      return dateFns
        .format(new Date(dateString), 'ddd', { locale: enLocale })
        .toUpperCase();
    },
    onInitChildCourse(argument) {
      this.$emit('init', argument);
    }
  }
};

function addBreakTimes(courseTimes, breakTimes) {
  let clonedCourseTimes = _.cloneDeep(courseTimes);

  breakTimes.forEach(breakTime => {
    clonedCourseTimes = addBreakTime(clonedCourseTimes, breakTime);
  });

  return clonedCourseTimes;
}

function addBreakTime(courseTimes, breakTime) {
  const splitedCourseTimes = courseTimes.map(courseTime => {
    return complementTime(courseTime, breakTime);
  });

  return _.concat(_.flatten(splitedCourseTimes));
}

function complementTime(courseTime, breakTime) {
  if (courseTime[0] < breakTime[0] && courseTime[1] > breakTime[1]) {
    return [[courseTime[0], breakTime[0]], [breakTime[1], courseTime[1]]];
  }

  if (courseTime[0] < breakTime[0] && courseTime[1] >= breakTime[0]) {
    return [[courseTime[0], breakTime[0]]];
  }

  if (courseTime[0] <= breakTime[1] && courseTime[1] > breakTime[1]) {
    return [[breakTime[1], courseTime[1]]];
  }

  if (courseTime[0] > breakTime[0] && courseTime[1] < breakTime[1]) {
    return [];
  }

  return [courseTime];
}
</script>

<style lang="scss">
.day-in-week {
  text-align: center;
  line-height: 40px;
  height: 40px;

  font-weight: bold;
  font-size: 16px;
  color: #666666;
  text-align: center;
}

.timetable-timeline {
  width: 60px;
  flex: initial;
}

.no-schedule-warning-message {
  width: 100%;
  display: flex;
  align-self: center;
  justify-content: center;

  .warning-message-container {
    transform: scale(1.5);
  }
}
</style>
